import {createContext, useContext, useEffect, useState} from 'react';
import {clearAdminSessionData, getAdminSessionData, getAdminSessionToken, getRememberLastUrl, isCurrUrlOfAuthRoute} from "../utils/AdminUtils";
import {checkNullJson, checkNullStr, getDefStr} from "../../common/fusion-ui/utils/ReactUtils";
import * as jwt_decode from 'jwt-decode';
import {useLocation} from "react-router-dom";
import {AdminBaseUrl, AdminGenRouteUrls, AdminModuleRouteUrls, AdminModules} from "../config/AdminConfig";
import {printError, printLog} from "../../common/CommonUtils";
import {SESSION_RECHECK_INTERVAL_SECONDS} from "../../common/CommonConfig";

const TAG = 'AdminAuthProvider';

/**
 * Context to handle admin authentication.
 * @type {React.Context<{ isAuthenticated: boolean }>}
 */
const AdminAuthContext = createContext();

/**
 * Provider component to handle admin authentications.
 * <p>
 * Manages the authentication state and redirections based on session validity.
 * @component
 * @param {object} props - React component props.
 * @param {React.ReactNode} props.children - Child components to be wrapped by the provider.
 */
function AdminAuthProvider({children}) {
    const location = useLocation();

    const [isAuthenticated, setIsAuthenticated] = useState(isSessionActive());
    const [isEmptyDataVerifDone, setIsEmptyDataVerifDone] = useState(false);

    const lastRememberUrl = getRememberLastUrl();

    useEffect(() => {
        if (!isEmptyDataVerifDone) {
            // Function to run perpetually
            const verifySession = () => {
                const fun = 'verifySession:';
                try {
                    const isSessionOn = isSessionActive();
                    // log(fun, 'isSessionOn:', isSessionOn);
                    if (isSessionOn) {
                        if (isCurrUrlOfAuthRoute(location)) {
                            window.location.href = getDefStr(lastRememberUrl, AdminModuleRouteUrls[AdminModules.Dashboard]);
                        }
                    } else {
                        //Perform actions when session isn't active
                        setIsEmptyDataVerifDone(true);
                        setIsAuthenticated(false);
                        clearAdminSessionData();

                        //If session isn't active & curr-route is neither auth-related nor some other specific routes(bcuz they have login-page in adminRoutes.js), then redirect to login
                        if (!isCurrUrlOfAuthRoute(location, null, true, [AdminModuleRouteUrls[AdminModules.Dashboard], AdminBaseUrl, AdminGenRouteUrls._404])) {
                            window.location.href = AdminGenRouteUrls.login;
                        }
                        //clear interval ID
                        try {
                            clearInterval(intervalId);
                        } catch (error) {
                            // logErr(fun,error);
                        }
                    }
                } catch (error) {
                    logErr(fun, error);
                }
            };

            // Run the function initially
            verifySession();

            // Set interval to run the function every 10 seconds
            const intervalId = setInterval(verifySession, SESSION_RECHECK_INTERVAL_SECONDS);

            // Cleanup function to clear interval when component unmounts
            return () => clearInterval(intervalId);
        }
    }, []);

    function isSessionActive() {
        const fun = 'isSessionActive:';
        const sessionDataFound = checkNullJson(getAdminSessionData());
        let tokenExpired = false;

        //check if token is expired or not
        if (sessionDataFound && checkNullStr(getAdminSessionToken())) {
            try {
                const decodedToken = jwt_decode.jwtDecode(getAdminSessionToken());
                const tokenExpTimestamp = decodedToken.exp;
                const currentTime = Date.now() / 1000;
                tokenExpired = tokenExpTimestamp < currentTime; // Token has expired
                // log(fun, 'sessionDataFound.token.decodedToken:', decodedToken, '\ntokenExpired:', tokenExpired, '\ncurrentTime:', currentTime, '\ncurrent-time:', timestampToReadableTime(currentTime), ', tokenExpTimestamp-time:', timestampToReadableTime(tokenExpTimestamp));
            } catch (error) {
                logErr(fun, 'error:', error);
                tokenExpired = true;
            }
        } else {
            tokenExpired = true;
        }
        return sessionDataFound && !tokenExpired;
    }

    return <AdminAuthContext.Provider value={{isAuthenticated}}>
        {children}
    </AdminAuthContext.Provider>
}

/**
 * Custom hook to access the auth context values.
 * @function
 * @returns {{ isAuthenticated: boolean }} context values.
 *
 * @example
 * import React from 'react';
 * import { useAdminAuth } from './AdminAuthProvider';
 *
 * const YourComponent = () => {
 *   const { isAuthenticated } = useAdminAuth();
 *
 *   const handleClick = () => {
 *     console.log('This is an auth message!', 'primary');
 *   };
 *
 *   return (
 *     <div>
 *       <button onClick={handleClick}>Test Auth</button>
 *     </div>
 *   );
 * };
 *
 * export default YourComponent;
 */
function useAdminAuth() {
    return useContext(AdminAuthContext);
}


function log(...text) {
    printLog(TAG, ...text);
}

function logErr(...text) {
    printError(TAG, ...text);
}

export {AdminAuthProvider, useAdminAuth};
